import { HUB_EDITOR_LEFT_SIDE_WIDTH } from '../Settings'
import { EditIcon, SectionIcon, SettingsIcon } from '../icons/Icons'
import { Link, useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { SectionObjectType, SectionType, WebsiteConfig, WebsiteRead, WebsiteSection } from '../api/dashboard'
import { ChangeSectionDialog } from './dialogs/ChangeSectionDialog'
import { ButtonIcon, TableRowEditButton } from '@/ui'
import { Trash } from '@/icons'
import { ReactSortable } from 'react-sortablejs'
import { Button, SelectItem, Skeleton, Toggle, cn, useModal } from '@dashboard/ui'
import { useTranslation } from 'react-i18next'
import { ThemeEditor } from '@/hub-editor/widgets/ThemeEditor'
import { ColorSchemeEditor } from '@/hub-editor/widgets/ColorSchemeEditor'
import { useFeatureFlags } from '@/libs'
import { Pallete } from '@/hub-editor/Pallete'
import { usePagesByIdsQuery } from '@/layouts/pages/api/usePagesByIdsQuery'

type EditTab = 'sections' | 'apps' | 'settings' | 'templates'

function SectionEditor(props: {
  webSite: WebsiteRead
  setWebSite: (webSite: WebsiteRead) => void
  sendIframeMessage: (data: { menuSections?: WebsiteSection[] }) => void
}) {
  const { t } = useTranslation()
  const { companyId, gameId } = useParams() as {
    companyId: string
    gameId: string
  }
  const { webSite, setWebSite } = props
  const usedPagesIds = webSite?.config?.sections?.filter(it => it.object_id).map(it => it.object_id)

  const { data: pages = [], isLoading } = usePagesByIdsQuery(companyId, gameId, {
    ids: usedPagesIds?.join(',') || '',
  })
  const sectionsList = useMemo(
    () =>
      webSite?.config?.sections?.filter(
        it =>
          (Object.values(SectionType).includes(it.name) && it.name !== SectionType.Page) ||
          (it.object_id && pages.find(p => p.id === it.object_id)) ||
          it.object_type == SectionObjectType.Link,
      ),
    [webSite?.config, pages],
  )

  const getSectionName = (section_id: string) => {
    return pages.find(p => p.id === section_id)?.title as SectionType
  }

  const getPageBySection = (section_id?: string) => {
    if (!section_id) {
      return
    }
    return pages.find(p => p.id === section_id)
  }
  const isSectionVisible = (s: WebsiteSection) => {
    let sections = sectionsList
    if (!sections) {
      return true
    }

    let section = s.object_id
      ? sections.find(it => it.object_id === s.object_id)
      : sections.find(it => it.name == s.name)
    if (!section) {
      return false
    }

    return section.visible
  }
  const onSectionVisibleChange = (s: WebsiteSection, value: boolean) => {
    let sections = sectionsList || []
    let section = s.object_id
      ? sections.find(it => it.object_id == s.object_id)
      : sections.find(it => it.name == s.name)

    if (!section) {
      section = { name: s.name, visible: value }
      sections.push(section)
    } else {
      section.visible = value
    }

    setWebSite({
      ...webSite,
      config: {
        ...webSite.config,
        sections: sections,
      },
    })
  }

  const findSectionByIndex = (section: WebsiteSection) => {
    const sections = sectionsList || []

    if (section.object_id) {
      return sections.findIndex(it => it.object_id == section.object_id)
    } else if (section.custom_url) {
      return sections.findIndex(it => it.custom_url == section.custom_url)
    } else {
      return sections.findIndex(it => it.name == section.name)
    }
  }

  const handleChange = (section: WebsiteSection, isNew: boolean) => {
    if (isNew) {
      setWebSite({
        ...webSite,
        config: {
          ...webSite.config,
          sections: [...(webSite.config?.sections || []), section],
        },
      })
      return
    }

    const sectionIndex = findSectionByIndex(section)
    const sections = sectionsList || []
    if (sectionIndex > -1) {
      sections[sectionIndex] = section
      setWebSite({
        ...webSite,
        config: {
          ...webSite.config,
          sections: sections,
        },
      })
    }
  }

  const handleDelete = (s: WebsiteSection) => {
    const sections = sectionsList || []
    const sectionIndex = findSectionByIndex(s)

    if (sectionIndex > -1) {
      sections.splice(sectionIndex, 1)
      setWebSite({
        ...webSite,
        config: {
          ...webSite.config,
          sections: sections,
        },
      })
    }
  }

  const handleMove = (s: WebsiteSection, direction: number) => {
    const sections = sectionsList || []
    const sectionIndex = findSectionByIndex(s)

    if (sectionIndex > -1) {
      sections.splice(sectionIndex + direction, 0, sections.splice(sectionIndex, 1)[0])
      setWebSite({
        ...webSite,
        config: {
          ...webSite.config,
          sections: sections,
        },
      })
    }
  }

  useEffect(() => {
    handleMenuChange()
  }, [sectionsList])

  const getPageSlugBySection = (section_id?: string) => {
    const page = getPageBySection(section_id)
    if (!page) {
      return
    }
    if (page.parent) {
      return `${page.parent.slug}/${page.slug}`
    }
    return page.slug
  }

  const handleMenuChange = () => {
    const hubSections = (sectionsList?.map(it => ({
      type: it.name,
      visible: it.visible,
      name: !it.object_id ? t(`section.${it.name}`) : getSectionName(it.object_id) || it.name,
      icon: it.icon,
      slug: getPageSlugBySection(it.object_id),
      custom_url: it.custom_url,
      custom_name: it.custom_name,
    })) || []) as WebsiteSection[]
    // send new sections to hub
    props.sendIframeMessage({
      menuSections: hubSections,
    })
  }

  const openEditSectionDialog = useModal<{ section: WebsiteSection; isNew: boolean }>(props => (
    <ChangeSectionDialog
      {...props}
      getPageBySection={getPageBySection}
      sectionsList={sectionsList}
      onSaved={data => handleChange(data, props.isNew)}
    />
  ))

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-3">
        {new Array(10).fill(0).map((_, idx) => (
          <Skeleton key={`editor-left-skeleton-${idx}`} className="h-8 w-full" />
        ))}
      </div>
    )
  }
  return (
    <div className="grow border-r border-border-secondary">
      <p className="border-b border-b-border-secondary px-4 py-3 font-semibold">{t('hub-settings.sections')}</p>
      <ReactSortable
        animation={200}
        delay={2}
        filter=".non-drag"
        list={sectionsList?.map(it => ({ ...it, id: it.name })) || []}
        setList={v => {
          if (webSite.config?.sections?.length !== v.length) {
            // this is not a drag and drop
            return
          }
          setWebSite({
            ...webSite,
            config: {
              ...webSite.config,
              sections: v,
            },
          })
        }}
      >
        {sectionsList?.map(it => {
          const actions = [] as SelectItem[]
          if (it.object_id || it.custom_url) {
            actions.push({
              children: t('edit'),
              value: 'edit',
              icon: EditIcon,
            })
            actions.push({
              value: 'delete',
              children: <span className="text-text-error-primary"> {t('delete')} </span>,
              icon: Trash,
            })
          }
          return (
            <div className="flex cursor-grab items-center gap-3 p-2 hover:text-black/50">
              <Toggle
                key={it.custom_name || it.name}
                size="sm"
                checked={isSectionVisible(it)}
                onChange={e => onSectionVisibleChange(it, e.target.checked)}
              />
              <div className="max-w-[100px] overflow-hidden whitespace-nowrap">
                {it.custom_name || (!it.object_id ? t(`section.${it.name}`) : getSectionName(it.object_id) || it.name)}
              </div>
              {actions.length > 0 && (
                <div className="ml-auto">
                  <TableRowEditButton
                    onChange={v => {
                      switch (v) {
                        case 'move-up':
                          handleMove(it, -1)
                          break
                        case 'move-down':
                          handleMove(it, +1)
                          break
                        case 'edit':
                          openEditSectionDialog({ section: it, isNew: false })
                          break
                        case 'delete':
                          handleDelete(it)
                          break
                      }
                    }}
                    options={actions}
                  />
                </div>
              )}
            </div>
          )
        })}
      </ReactSortable>

      <div className="m-2">
        <Button
          className="w-full"
          onClick={() => openEditSectionDialog({ section: {} as WebsiteSection, isNew: true })}
        >
          {t('hub-settings.sections.add')}
        </Button>
      </div>
    </div>
  )
}

export function LeftSideBar(props: {
  webSite: WebsiteRead
  setWebSite: (webSite: WebsiteRead) => void
  sendIframeMessage: (data: { menuSections?: WebsiteSection[] }) => void
  previewConfig: WebsiteConfig
  setPreviewConfig: (config: WebsiteConfig) => void
  updateTheme: (css: string) => void
  findToken: (token: string) => void
  handleTokensPage: () => void
}) {
  const { featureFlags } = useFeatureFlags()
  const { webSite, setWebSite } = props

  const [activeTab, setActiveTab] = useState<EditTab>('settings')

  const renderThemeEditor = () => {
    return (
      <ThemeEditor
        website={webSite}
        updateTheme={props.updateTheme}
        findToken={props.findToken}
        handleTokensPage={props.handleTokensPage}
      />
    )
  }

  return (
    <div
      className="flex h-full"
      style={{
        width: HUB_EDITOR_LEFT_SIDE_WIDTH,
        minWidth: HUB_EDITOR_LEFT_SIDE_WIDTH,
      }}
    >
      <div className="flex w-16 flex-col justify-start gap-1 border-r border-border-secondary p-1">
        {[
          {
            title: '',
            icon: <SettingsIcon />,
            href: '',
            value: 'settings',
          },
          {
            title: '',
            icon: <SectionIcon />,
            href: '',
            value: 'sections',
          },
        ].map((it, idx) => (
          <Link key={`sidebar-link-${idx}`} to={it.href}>
            <ButtonIcon
              className={cn(
                'w-full border-none',
                it.value == activeTab
                  ? 'bg-fg-brand-secondary text-text-brand-primary hover:bg-fg-brand-tertiary-hover'
                  : 'bg-fg-primary text-text-tertiary hover:bg-fg-secondary-hover',
              )}
              onClick={() => {
                setActiveTab(it.value as EditTab)
                props.setPreviewConfig({})
              }}
            >
              {it.icon}
            </ButtonIcon>
          </Link>
        ))}

        {featureFlags.override_colors_enabled && (
          <div className="mt-auto">
            <Link to={''}>
              <ButtonIcon
                className={cn(
                  'w-full border-none',
                  activeTab == 'templates'
                    ? 'bg-fg-brand-secondary text-text-brand-primary hover:bg-fg-brand-tertiary-hover'
                    : 'bg-fg-primary text-text-tertiary hover:bg-fg-secondary-hover',
                )}
                onClick={() => {
                  setActiveTab('templates')
                  props.setPreviewConfig({})
                }}
              >
                <Pallete />
              </ButtonIcon>
            </Link>
          </div>
        )}
      </div>

      <div className="size-full overflow-y-scroll">
        {activeTab == 'sections' && (
          <SectionEditor webSite={webSite} setWebSite={setWebSite} sendIframeMessage={props.sendIframeMessage} />
        )}
        {activeTab == 'settings' && renderThemeEditor()}

        {activeTab == 'templates' && (
          <ColorSchemeEditor
            website={webSite}
            updateTheme={props.updateTheme}
            onChange={v => {
              props.setPreviewConfig(v)
            }}
            handleTokensPage={props.handleTokensPage}
          />
        )}
      </div>
    </div>
  )
}
